import {
  registerUser,
  loginCognitoUser,
  registerCognitoUser,
  resendCodeCognitoUser,
  confirmSignUpCognitoUser,
} from "api/user";
import Loader from "components/loader";
import { toast } from "react-toastify";
import { useMutation } from "react-query";
import { useEffect, useState } from "react";
import useUserSession from "zustand-store/user";
import { getCurrentUser } from "aws-amplify/auth";
import SetupProfile from "components/set-up-profile";
import ConfirmSignup from "components/confirm-signup";
import SignupForm from "sections/register/signup-form";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ValuePropPanel from "components/auth/value-prop-panel";
import FeatureTicker from "components/auth/feature-ticker";

const MOBILE_AVATARS = ["SM", "JT", "RL"];

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, setIsAuthenticated } = useUserSession();

  const planValue = searchParams.get("plan");

  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showSetupProfile, setShowSetupProfile] = useState(false);
  const [showConfirmSignup, setShowConfirmSignup] = useState(false);

  const registerUserMutation = useMutation(registerUser, {
    onSuccess: async () => {
      try {
        await loginCognitoUser(userData.email, userData.password);
        setShowSetupProfile(true);
        setShowConfirmSignup(false);
      } catch (error) {
        console.error("Error signing in:", error);
        navigate("/login", { replace: true });
      }
    },
    onError: () => {
      setLoading(false);
      toast.error("Some error occured", { position: "top-right" });
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const onSubmitRegister = async (data) => {
    try {
      setLoading(true);
      const user = await registerCognitoUser(data);
      setUserData({ ...data, cognitoUserId: user.userId });
      setShowConfirmSignup(true);
    } catch (error) {
      // A Cognito user already exists for this email — almost always because a
      // previous attempt was abandoned at the OTP step (unconfirmed) or already
      // completed. Instead of a dead-end error, send the user to login, which
      // already handles both cases (re-verify unconfirmed accounts, or sign in
      // confirmed ones).
      const isExistingUser =
        error?.name === "UsernameExistsException" ||
        String(error?.message || error).includes("UsernameExistsException");

      if (isExistingUser) {
        toast.info(
          "You've already started signing up with this email. Please log in to continue.",
          { position: "top-right" }
        );
        navigate("/login", { replace: true });
        return;
      }

      toast.error(error.message || "Signup failed. Please try again!", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmitConfirmSignup = async (data) => {
    try {
      setLoading(true);
      const { isSignUpComplete } = await confirmSignUpCognitoUser(
        userData.email,
        data.confirmationCode
      );
      if (isSignUpComplete) {
        registerUserMutation.mutate({
          email: userData.email,
          lastName: userData.lastName,
          firstName: userData.firstName,
          cognitoId: userData.cognitoUserId,
          role: "USER",
          isTrial: true,
          isDeleted: false,
        });
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      if (error.toString().includes("CodeMismatchException")) {
        toast.error("Invalid OTP", { position: "top-right" });
        return;
      }
      toast.error("OTP verification failed", { position: "top-right" });
    }
  };

  const handleResendCode = async (successCallback) => {
    try {
      await resendCodeCognitoUser(userData.email);
      successCallback();
    } catch (error) {
      console.error("Failed to resend code:", error);
      toast.error("Failed to resend code. Please try again.", {
        position: "top-right",
      });
    }
  };

  useEffect(() => {
    const checkIfLoggedIn = async () => {
      try {
        await getCurrentUser();
        setIsAuthenticated(true);
      } catch (error) {
        console.error("Error checking user status:", error);
        setIsAuthenticated(false);
      }
    };

    if (planValue) checkIfLoggedIn();
  }, [planValue, setIsAuthenticated]);

  if (isAuthenticated === null && planValue) return <Loader />;

  if (isAuthenticated && planValue) {
    return (
      <Navigate
        replace
        to={planValue ? `/subscription?plan=${planValue}` : "/create-notes"}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row font-dm-sans">
      <Helmet>
        <title>Register | NirvaScribe — AI Medical Scribe for Clinicians</title>
        <meta
          name="description"
          content="Sign up for NirvaScribe to create real, compliant doctors notes and medical certificates in Australia. Fast, AI-assisted documentation."
        />
        <link
          rel="canonical"
          href="https://app.nirvascribe.com/register"
        />
      </Helmet>

      {/* Desktop: left value-prop panel — hidden once setup begins */}
      {!showSetupProfile && (
        <div className="hidden md:block md:w-1/2 shrink-0">
          <ValuePropPanel />
        </div>
      )}

      {/* Mobile: compact header — hidden once setup begins */}
      {!showSetupProfile && <div className="md:hidden bg-gradient-to-b from-[#e8fbfc] via-primary to-[#7de0e6] relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute top-0 right-0 w-40 h-40 rounded-full bg-[rgba(0,180,200,0.18)] blur-3xl pointer-events-none"
        />
        <div className="relative z-10 px-5 pt-4 pb-0">
          <div className="flex items-center justify-between mb-3">
            <img src="/logo-name.png" alt="NirvaScribe" className="h-7" />
            <div className="flex items-center gap-1.5">
              <div className="flex -space-x-1.5">
                {MOBILE_AVATARS.map((init, i) => (
                  <div
                    key={i}
                    className="w-[22px] h-[22px] rounded-full bg-white/70 border-2 border-[#7de0e6] flex items-center justify-center text-[8px] text-gray-800 font-semibold"
                  >
                    {init}
                  </div>
                ))}
              </div>
              <span className="text-[10px] text-gray-600">Aussie clinicians</span>
            </div>
          </div>
          <h1 className="text-[22px] font-bold text-gray-900 leading-tight mb-3">
            Focus on Care, Not Paperwork.
          </h1>
          <FeatureTicker dark={false} />
        </div>
      </div>}

      {/* Right panel: form */}
      <div className="flex-1 md:w-1/2 bg-white md:bg-brand-off-white flex flex-col md:items-center md:justify-center md:p-6 overflow-y-auto">
        <div className="w-full md:max-w-[440px] md:bg-white md:rounded-xl md:shadow-auth-card p-6 md:p-7">
          {/* Logo on card — desktop only */}
          <div className="hidden md:block mb-5">
            <img src="/logo-name.png" alt="NirvaScribe" className="h-8" />
          </div>

          {showConfirmSignup ? (
            <ConfirmSignup
              loading={loading}
              onSubmit={onSubmitConfirmSignup}
              handleResendCode={handleResendCode}
            />
          ) : showSetupProfile ? (
            <SetupProfile />
          ) : (
            <SignupForm onSubmit={onSubmitRegister} loading={loading} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
